import React from 'react';
import styled, { css } from 'styled-components';
import { rem } from 'polished';
import { GatsbyImage, getImage, IGatsbyImageData } from 'gatsby-plugin-image';
import { Text } from '../Text';
import { Rectangle } from '../Rectangle';
import { MdContent } from '../MdContent';

type Mode = 'horizontal' | 'vertical';

interface IStyledServiceThumbnail {
  mode: Mode;
}

const StyledServiceThumbnail = styled.div<IStyledServiceThumbnail>`
  flex: 1 0 auto;
  display: flex;
  overflow: hidden;
  border-radius: ${rem(`12px`)};
  transition: box-shadow 0.8s, transform 0.6s;
  width: auto;
  height: auto;
  ${({ theme }) => theme.breakpoints.desktop`
    width: auto;
    height: auto;
    &:hover {
      cursor: pointer;
      box-shadow: 0px 0px 20px rgba(246, 56, 251, 0.5);
      .service-thumbnail__container::after {
        opacity: 1;
        transform: rotate(-45deg) translateY(100%);
      }
      .service-thumbnail__title {
        color: ${theme.colors.yellow_1};
      }
      .service-thumbnail__icon {
        filter: drop-shadow(0px 0px ${rem(`20px`)} ${
    theme.colors.blue_1
  } ) contrast(110%);
      }
    }
  `}
  .service-thumbnail__container {
    width: 100%;
    flex: 1 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: ${rem(`23px`)};
    border-radius: ${rem(`12px`)};
    margin: 1px;
    ::after {
      content: '';
      position: absolute;
      top: -25%;
      left: -25%;
      width: 150%;
      height: 150%;
      background: linear-gradient(
        0deg,
        transparent,
        transparent 30%,
        rgba(246, 56, 251, 0.3)
      );
      transform: rotate(-45deg);
      transition: all 0.6s ease;
      opacity: 0;
    }
  }
  .service-thumbnail__content {
    padding: ${rem(`24px`)} 0 0 0;
    display: flex;
    flex: 0 1 auto;
    width: 100%;
    flex-direction: column;
  }
  .service-thumbnail__image-container {
    display: flex;
    flex: 1 0 auto;
    align-items: center;
  }
  .service-thumbnail__icon {
    width: 100%;
    height: 100%;
    max-width: ${rem(`180px`)};
    flex: 0 1 auto;
    filter: drop-shadow(
        0px 0px ${rem(`1px`)} ${({ theme }) => theme.colors.pink_2_50}
      )
      contrast(115%);
    transition: filter 500ms ease-in-out;
  }
  .service-thumbnail__title {
    font-size: ${rem(`20px`)};
    line-height: ${rem(`20px`)};
    width: 100%;
    transition: color 500ms ease-in-out;
    margin-bottom: ${rem(`12px`)};
    ${({ theme }) => theme.breakpoints.desktop`
      font-size: ${rem(`24px`)};
      line-height: ${rem(`24px`)};
    `}
  }
  .service-thumbnail__body {
    display: flex;
    width: 100%;
    margin-bottom: ${rem(`12px`)};
  }
  .service-thumbnail__children {
    display: flex;
    flex: 1 0 auto;
    justify-content: flex-end;
    align-items: flex-end;
    width: 100%;
  }

  ${({ mode, theme }) =>
    mode === `horizontal` &&
    css`
      width: 100%;
      height: 100%;
      .service-thumbnail__container {
        flex-wrap: wrap;
      }
      .service-thumbnail__content {
        flex: 1 0 auto;
        padding: ${rem(`24px`)} 0 0 0;
      }
      .service-thumbnail__image-container {
        flex: 0 1 auto;
      }
      ${theme.breakpoints.desktop`
        width: 100%;
        height: 100%;
        &:hover {
          .service-thumbnail__container::after {
            transform: rotate(-45deg) translateY(150%);
          }
        }
        .service-thumbnail__container {
          flex-direction: row;
          justify-content: left;
          align-items: left;
          flex-wrap: nowrap;
        }
        .service-thumbnail__content {
          height: 100%;
          flex: 0 1 auto;
          padding: 0 0 0 ${rem(`24px`)};
        }
        .service-thumbnail__image-container {
          flex: 1 0 auto;
        }
        .service-thumbnail__image-container {
          flex: 1 0 auto;
        }
      `}
    `}

  #augmentation_animated *,
  #apis_animated *,
  #webapps_animated *,
  #mobileapps_animated *,
  #softwaredevelopment_animated *,
  #consulting_animated * {
    animation-play-state: paused !important;
  }
  &:hover {
    #augmentation_animated *,
    #apis_animated *,
    #webapps_animated *,
    #mobileapps_animated *,
    #softwaredevelopment_animated *,
    #consulting_animated * {
      animation-play-state: running !important;
    }
  }
`;

export interface IServiceThumbnail {
  animated?: {
    svg: {
      originalContent: string;
    };
  };
  icon?: {
    description: string;
    gatsbyImageData: IGatsbyImageData;
  };
  name: string;
  className?: string;
  body?: string;
  mode?: Mode;
  children?: any;
}

export const ServiceThumbnail = ({
  animated,
  icon,
  name,
  className,
  body,
  mode = `vertical`,
  children,
}: IServiceThumbnail) => {
  const image = icon && getImage(icon?.gatsbyImageData);
  const svgContent = animated?.svg.originalContent;

  return (
    <StyledServiceThumbnail className={className} mode={mode}>
      <Rectangle className="service-thumbnail__container">
        <div className="service-thumbnail__image-container">
          {icon && (
            <GatsbyImage
              image={image}
              alt={`icon for ${icon.description}`}
              className="service-thumbnail__icon"
            />
          )}
          {animated && (
            <div
              style={{ width: `180px`, height: `182px` }}
              className="service-thumbnail__icon"
              dangerouslySetInnerHTML={{ __html: svgContent }}
            />
          )}
        </div>

        <div className="service-thumbnail__content">
          <Text
            type="title3"
            tag="h4"
            color="white_1"
            noRhythem
            className="service-thumbnail__title"
          >
            {name}
          </Text>
          {body && (
            <Text
              noRhythem
              type="body3"
              tag="div"
              color="gray_3"
              className="service-thumbnail__body"
            >
              <MdContent content={body} />
            </Text>
          )}
          {children && (
            <div className="service-thumbnail__children">{children}</div>
          )}
        </div>
      </Rectangle>
    </StyledServiceThumbnail>
  );
};
